import { useCallback, useEffect, useState } from "react";
import {
  computeMulchEstimate,
  getCurrentMulchMap,
  getServiceAvailability,
  saveMulchMap,
} from "api/MulchMap";
import {
  ComputeEstimateInput,
  EstimateBreakdown,
  MulchMap,
  SaveMulchMapInput,
  ServiceAvailability,
} from "api/MulchMap/types";

/**
 * Load / save the current version of a Map at a Location.
 *
 * `mapGroupId` scopes the fetch to a specific Map in the multi-variant
 * world. When it's omitted (legacy paths like `/mulch-calculator/:id/edit`),
 * the backend returns the most-recently-touched current row at the
 * location — the "alias" behavior.
 */
export function useMulchMap(
  locationId: string | null,
  mapGroupId?: string | null,
) {
  const [map, setMap] = useState<MulchMap | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refetch = useCallback(async () => {
    if (!locationId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await getCurrentMulchMap(locationId, mapGroupId || undefined);
      setMap(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load mulch map.");
    } finally {
      setLoading(false);
    }
  }, [locationId, mapGroupId]);

  useEffect(() => {
    if (locationId) refetch();
    else setMap(null);
  }, [locationId, mapGroupId, refetch]);

  // Session 23: return the full result object (MulchMap on success or
  // { error, status, body } on failure) so callers can toast the real
  // backend message. Returning null-on-failure erased the diagnostic
  // information the API layer just collected.
  const save = useCallback(
    async (
      input: SaveMulchMapInput,
    ): Promise<
      MulchMap | { error: string; status?: number; body?: any } | null
    > => {
      setSaving(true);
      setError(null);
      try {
        const res = await saveMulchMap(input);
        if (res && "id" in (res as any)) {
          setMap(res as MulchMap);
          return res as MulchMap;
        }
        const errMsg =
          (res as any)?.error ||
          (res as any)?.message ||
          "Failed to save.";
        setError(errMsg);
        return (res as any) ?? { error: errMsg };
      } finally {
        setSaving(false);
      }
    },
    [],
  );

  return { map, loading, saving, error, refetch, save };
}

export function useServiceAvailability(
  locationId: string | null,
  productId?: string,
) {
  const [availability, setAvailability] =
    useState<ServiceAvailability | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!locationId) {
      setAvailability(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    getServiceAvailability(locationId, productId).then((res) => {
      if (!cancelled) {
        setAvailability(res);
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [locationId, productId]);

  return { availability, loading };
}

export function useEstimate() {
  const [estimate, setEstimate] = useState<EstimateBreakdown | null>(null);
  const [computing, setComputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const compute = useCallback(async (input: ComputeEstimateInput) => {
    setComputing(true);
    setError(null);
    try {
      const res = await computeMulchEstimate(input);
      if ("totals" in (res as any)) {
        setEstimate(res as EstimateBreakdown);
        return res as EstimateBreakdown;
      }
      setError((res as any)?.message || (res as any)?.error || "Failed.");
      setEstimate(null);
      return null;
    } finally {
      setComputing(false);
    }
  }, []);

  const clear = useCallback(() => {
    setEstimate(null);
    setError(null);
  }, []);

  return { estimate, computing, error, compute, clear };
}
